<script setup lang="ts">
import { watch } from 'vue'
import { storeToRefs } from 'pinia'
import { logService } from '@nsftx/seven-client-retail-utils'
import { useRouteLayout } from '@nsftx/seven-client-retail-vue-common'
import {
  bindEmitKeydown,
  bindShortcutKeys,
  unbindEmitKeydown,
  unbindShortcutKeys,
  useSpinnerStore,
} from '@nsftx/seven-nxg-retail-vue-sdk'
import { useDmCoreSpaceOperatorStore } from '@nsftx/seven-client-retail-vue-dm-core'
import {
  NXGRetailLoadingOverlay,
  NXGRetailNotifications,
  NXGRetailPosDocumentViewerModal,
} from '@nsftx/seven-nxg-retail-vue-sdk/ui'
import { getDeviceAccessToken } from '@nsftx/seven-client-retail-vue-dm'
import { useAppBootstrap } from '@/composables/useAppBootstrap/useAppBootstrap'
import { registerSlipPayinHandlers } from '@/modules/slip-payin/slipPayinService'
import { registerCalculatorSlipNotificationHandlers } from '@/modules/calculator/calculatorSlipNotificationService'
import '../../styles/main.scss'
import SSAppLayout from '../layouts/SSAppLayout.vue'
import SSMainMenuModal from './SSMainMenuModal.vue'
import SSSlipCheckModal from './slip-check/SSSlipCheckModal.vue'
import SSSlipDetailsModal from './slip-check/SSSlipDetailsModal.vue'
import SSVoucherDetailsModal from './voucher/SSVoucherDetailsModal.vue'
import { posLayouts, type PosLayouts } from '../layouts'
import router from '@/router'
import { useOpenGameShortcut } from '@/composables/useOpenGameShortcut/useOpenGameShortcut'
import { useOperationNavigationShortcuts } from '@/composables/useOperationNavigationShortcuts/useOperationNavigationShortcuts'
import { useWidgetShortcuts } from '@/composables/useWidgetShortcuts/useWidgetShortcuts'

const LOG_PREFIX = '[App.vue]'

const { isSpinnerOpened } = storeToRefs(useSpinnerStore())
const { isOperatorLoggedIn } = storeToRefs(useDmCoreSpaceOperatorStore())
const { startApp, handleDeviceAccessTokenReceived } = useAppBootstrap()
const { registerLayouts } = useRouteLayout<PosLayouts>()

const accessToken = getDeviceAccessToken()

watch(isOperatorLoggedIn, (loggedIn) => {
  if (loggedIn) {
    bindShortcutKeys()
    bindEmitKeydown()
    return
  }

  unbindShortcutKeys()
  unbindEmitKeydown()
})

watch(accessToken, (token) => {
  logService.info(`${LOG_PREFIX} Access token updated`, {
    code: 'SS_ACCESS_TOKEN_UPDATED',
    details: {
      hasToken: !!token,
    },
  })
  if (token) handleDeviceAccessTokenReceived(token)
})

startApp()
registerSlipPayinHandlers()
registerCalculatorSlipNotificationHandlers()
registerLayouts(posLayouts, { router, defaultLayout: 'BaseLayout' })

useOpenGameShortcut()
useOperationNavigationShortcuts()
useWidgetShortcuts()
</script>

<template>
  <div class="ss-app">
    <SSAppLayout />
    <NXGRetailNotifications position="topMiddle" />
    <NXGRetailLoadingOverlay :visible="isSpinnerOpened" />
    <SSMainMenuModal />
    <SSSlipCheckModal />
    <SSSlipDetailsModal />
    <SSVoucherDetailsModal />
    <NXGRetailPosDocumentViewerModal />
  </div>
</template>

<style lang="scss">
.ss-app {
  display: grid;
  grid-template-rows: auto 1fr auto;
  grid-template-areas:
    'header'
    'main'
    'footer';
  height: 100vh;
  overflow: hidden;
  padding: var(--nxgds-alias-spacing-3xs);

  &__main {
    grid-area: main;
    overflow: auto;
    scrollbar-gutter: auto;
    background-color: var(--nxgds-mapped-color-surface-component-page);
  }
}
</style>
